import React, { useState } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { PhotoIcon, TrashIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';

const ImageManager = () => {
  const { images, uploadImage } = useAdmin();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [imageKey, setImageKey] = useState('');

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleUpload = () => {
    if (selectedFile && imageKey) {
      const reader = new FileReader();
      reader.onloadend = () => {
        uploadImage(imageKey, reader.result);
        setSelectedFile(null);
        setPreview(null);
        setImageKey('');
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleDelete = (key) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      uploadImage(key, null);
    }
  };

  const imageCategories = [
    'hero-background',
    'about-image',
    'services-hero',
    'testimonial-bg',
    'contact-office',
    'logo-main',
    'logo-dark',
    'team-lead',
    'office-exterior',
    'office-interior'
  ];

  return (
    <div className="section-padding">
      <div className="container-custom">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-naqd-light-text dark:text-naqd-dark-text mb-2">
              Image Manager
            </h1>
            <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
              Upload and manage your website images
            </p>
          </div>

          {/* Upload Section */}
          <div className="card mb-8">
            <h3 className="text-xl font-semibold mb-4 text-naqd-light-text dark:text-naqd-dark-text">
              Upload New Image
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                  Image Category
                </label>
                <select
                  value={imageKey}
                  onChange={(e) => setImageKey(e.target.value)}
                  className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text"
                >
                  <option value="">Select category...</option>
                  {imageCategories.map(cat => (
                    <option key={cat} value={cat}>{cat.replace('-', ' ').toUpperCase()}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                  Select Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-naqd-primary file:text-white hover:file:bg-naqd-primary/80 bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text"
                />
              </div>
            </div>

            {preview && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                  Preview
                </label>
                <img src={preview} alt="Preview" className="max-w-xs h-auto rounded-lg border border-naqd-light-border dark:border-naqd-dark-border" />
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={!selectedFile || !imageKey}
              className="mt-4 px-4 py-2 bg-naqd-primary text-white rounded-lg hover:bg-naqd-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <CloudArrowUpIcon className="w-4 h-4 mr-2" />
              Upload Image
            </button>
          </div>

          {/* Existing Images */}
          <div className="card">
            <h3 className="text-xl font-semibold mb-4 text-naqd-light-text dark:text-naqd-dark-text">
              Current Images
            </h3>
            
            {Object.keys(images).length === 0 ? (
              <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                No images uploaded yet. Upload some images to get started.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Object.entries(images).map(([key, data]) => (
                  data && (
                    <div key={key} className="group relative">
                      <img 
                        src={data} 
                        alt={key} 
                        className="w-full h-32 object-cover rounded-lg border border-naqd-light-border dark:border-naqd-dark-border"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
                        <button
                          onClick={() => handleDelete(key)}
                          className="opacity-0 group-hover:opacity-100 bg-red-500 text-white p-2 rounded-full transition-opacity"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm font-medium text-naqd-light-text dark:text-naqd-dark-text">
                          {key.replace('-', ' ').toUpperCase()}
                        </p>
                        <p className="text-xs text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                          Click to view full size
                        </p>
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageManager;